import { html, attr, type Signal, Fragment, When, Ensure } from '@tempots/dom'
import { Anchor } from '@tempots/ui'
import { Icon, ButtonLink } from '@tempots/beatui'
import { files as filesService } from '@/services/gateway'
import { Workspace } from '@/state/workspace'
import { safeJsonStringify } from '@/utils'
import type { ActionExecuteResponse } from '@/services/types'
import type { ExecuteError } from '../hooks/use-action-detail'

export interface ExecuteResultOptions {
  executeResult: Signal<
    (ActionExecuteResponse & { success: true }) | ExecuteError | null
  >
}

export function ExecuteResult({ executeResult }: ExecuteResultOptions) {
  return Ensure(executeResult, (executeResult) =>
    html.div(
      attr.class(
        executeResult.map((r) =>
          r?.success
            ? 'bg-green-50 border border-green-200 rounded-lg p-4 mb-4 space-y-3'
            : 'bg-red-50 border border-red-200 rounded-lg p-4 mb-4'
        )
      ),
      // Success/Error header
      html.div(
        attr.class('flex items-center gap-2'),
        When(
          executeResult.map((r) => r?.success === true),
          () =>
            Fragment(
              Icon(
                { icon: 'mdi:check-circle' },
                attr.class('w-5 h-5 text-green-600')
              ),
              html.span(
                attr.class('text-green-800 font-medium text-sm'),
                'Execution completed'
              ),
              html.span(
                attr.class('text-green-600 text-sm ml-auto'),
                executeResult.map((r) =>
                  r?.success ? `${r.creditsCharged} credits` : ''
                )
              )
            ),
          () =>
            // Check if it's an insufficient credits error
            When(
              executeResult.map(
                (r) => !r?.success && r?.errorType === 'insufficient_credits'
              ),
              // Insufficient credits - show detailed message with link to billing
              () =>
                html.div(
                  attr.class('space-y-3'),
                  html.div(
                    attr.class('flex items-center gap-2'),
                    Icon(
                      { icon: 'mdi:credit-card-off' },
                      attr.class('w-5 h-5 text-red-600')
                    ),
                    html.span(
                      attr.class('text-red-800 font-medium text-sm'),
                      'Insufficient Credits'
                    )
                  ),
                  html.p(
                    attr.class('text-sm text-red-700'),
                    executeResult.map((r) => {
                      if (r?.success || r?.errorType !== 'insufficient_credits')
                        return ''
                      const available = r.availableCredits
                      const required = r.requiredCredits
                      if (available !== undefined && required !== undefined) {
                        return `You need ${required} credits to execute this action, but only have ${available} credits available.`
                      }
                      return "You don't have enough credits to execute this action."
                    })
                  ),
                  ButtonLink(
                    {
                      size: 'sm',
                      href: Workspace.organizationId.map((orgId) =>
                        orgId ? `${Workspace.getOrgPath(orgId)}/billing` : '#'
                      ),
                    },
                    Icon(
                      { icon: 'mdi:credit-card-plus' },
                      attr.class('w-4 h-4 mr-2')
                    ),
                    'Add Credits'
                  )
                ),
              // Generic error
              () =>
                Fragment(
                  Icon(
                    { icon: 'mdi:alert-circle' },
                    attr.class('w-5 h-5 text-red-600')
                  ),
                  html.span(
                    attr.class('text-red-800 font-medium text-sm'),
                    executeResult.map((r) =>
                      !r?.success ? (r?.error ?? 'Execution failed') : ''
                    )
                  )
                )
            )
        )
      ),
      // Output file download link (if present)
      When(
        executeResult.map((r) => r?.success === true && !!r.outputFileHash),
        () =>
          html.div(
            attr.class(
              'flex items-center gap-2 bg-white rounded-md p-2 border border-green-200'
            ),
            Icon(
              { icon: 'mdi:file-download' },
              attr.class('w-4 h-4 text-green-600')
            ),
            html.span(attr.class('text-sm text-gray-700'), 'Output file:'),
            Anchor(
              {
                href: executeResult.map((r) =>
                  r?.success && r.outputFileHash
                    ? filesService.getContentUrl(r.outputFileHash)
                    : '#'
                ),
              },
              attr.class(
                'text-sm text-brand-600 hover:text-brand-800 underline font-mono'
              ),
              attr.target('_blank'),
              executeResult.map((r) =>
                r?.success && r.outputFileHash
                  ? r.outputFileHash.slice(0, 12) + '...'
                  : ''
              )
            )
          )
      ),
      // JSON result (if present)
      When(
        executeResult.map(
          (r) =>
            r?.success === true &&
            r.result !== undefined &&
            Object.keys(r.result).length > 0
        ),
        () =>
          html.div(
            attr.class('bg-white rounded-md border border-green-200'),
            html.div(
              attr.class(
                'px-3 py-2 border-b border-green-200 flex items-center gap-2'
              ),
              Icon(
                { icon: 'mdi:code-json' },
                attr.class('w-4 h-4 text-gray-500')
              ),
              html.span(
                attr.class('text-xs font-medium text-gray-600'),
                'Result'
              )
            ),
            html.pre(
              attr.class(
                'p-3 text-xs font-mono text-gray-800 overflow-x-auto max-h-64 overflow-y-auto'
              ),
              executeResult.map((r) =>
                r?.success && r.result ? safeJsonStringify(r.result) : ''
              )
            )
          )
      )
    )
  )
}
